import React, { useState } from "react";
import tw, { styled } from "twin.macro";
import _ from "lodash";
import moment from "moment-timezone";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import Carousel from "nuka-carousel/lib/carousel";
import {
  getContrastText,
  getNextEvent,
  getNextGame,
  hasEmptyDeep,
  extractContentInsideBrackets,
} from "../utils";
import useStats from "../hooks/useStats";
import { TZ } from "../constants";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import useIsClient from "../hooks/useIsClient";
import { Popover } from "react-tiny-popover";
import logoPlaceholder from "../images/no-image_baseball.png";

const SliderContainer = styled.div`
  ${tw`flex-1`}
`;
const EmptyContainer = styled.div`
  ${tw`h-[88px] mb-4 border-t-[16px] border-white  rounded cursor-progress bg-slate-200 animate-pulse`}
`;
const Svg = styled.img`
  ${tw`w-8 h-8`}
`;
const LeftButton = styled.button`
  ${tw`absolute left-[-30px] top-0 bottom-0 my-auto`}
  &:disabled > img {
    opacity: 0.2;
  }
`;
const RightButton = styled.button`
  ${tw`absolute right-[-30px] top-0 bottom-0 my-auto`}
  &:disabled > img {
    opacity: 0.2;
  }
`;
const GAME_DURATION_MINUTE = 180;

const ScheduleSlider = ({ data, site, color, homeTeams }) => {
  const { isClient, key } = useIsClient();
  const breakpoints = useBreakpoint();

  const teamsByName = _.keyBy(data.allTeams.edges, (edge) =>
    (
      _.get(edge, "node.frontmatter.id", "") ||
      _.get(edge, "node.frontmatter.name", "")
    ).toLowerCase()
  );
  const allLeagues = _.get(data, "allLeagues.edges", []);

  const allGames = _.get(data, "allGames.nodes", []);
  const allEvents = _.get(data, "allEvents.edges", []);

  // find the index of the most recent completed game and use it as the initial slide on carousel
  const initialIndex = getInitialGameIndex(allGames, GAME_DURATION_MINUTE);
  const startingIndex = Math.max(initialIndex - 14, 0);
  const endingIndex = Math.min(startingIndex + 40, allGames.length);
  const initialIndexForFilteredGames = initialIndex - startingIndex;

  // calculate slides to show based on screen width
  const slidesToShow = getSlidesToShow(breakpoints);

  // limit amount of games passed to slider to 21 games
  const slicedGames = allGames.slice(startingIndex, endingIndex + 1);
  const nextGame = getNextGame(allGames);
  const nextEvent = getNextEvent(allEvents);
  let gamesAndEvents = [...slicedGames];
  if (_.isNil(nextGame) && !_.isNil(nextEvent)) {
    gamesAndEvents = [...slicedGames, nextEvent];
  }
  let watchLiveLink = _.get(
    data,
    "markdownRemark.frontmatter.hero.watchLiveLink",
    ""
  );
  let teamSeasonLink = _.get(
    data,
    "markdownRemark.frontmatter.hero.teamSeasonLink",
    ""
  );

  if (site === "academy" && (homeTeams || []).length === 1) {
    const currentTeam = teamsByName[homeTeams[0]];
    watchLiveLink = _.get(currentTeam, "node.frontmatter.watchLiveLink", "");
    teamSeasonLink = _.get(currentTeam, "node.frontmatter.teamSeasonLink", "");
  }

  if (!isClient) return <EmptyContainer />;
  return (
    <SliderContainer key={key}>
      {!_.isEmpty(data.allGames.nodes) && (
        <Carousel
          slidesToShow={slidesToShow}
          renderBottomCenterControls={null}
          renderCenterLeftControls={({
            previousDisabled,
            goToSlide,
            currentSlide,
          }) => (
            <LeftButton
              disabled={previousDisabled}
              onClick={() =>
                goToSlide(
                  currentSlide - Math.floor(slidesToShow) < 0
                    ? 0
                    : currentSlide - Math.floor(slidesToShow)
                )
              }
            >
              <Svg src="/img/svg/chevron_left.svg" alt="previous_button" />
            </LeftButton>
          )}
          renderCenterRightControls={({
            nextDisabled,
            goToSlide,
            currentSlide,
            slideCount,
          }) => (
            <RightButton
              disabled={nextDisabled}
              onClick={() =>
                goToSlide(
                  currentSlide + Math.floor(slidesToShow) > slideCount - 1
                    ? slideCount - 1
                    : currentSlide + Math.floor(slidesToShow)
                )
              }
            >
              <Svg src="/img/svg/chevron_right.svg" alt="next_button" />
            </RightButton>
          )}
          slideIndex={initialIndexForFilteredGames}
        >
          {["academy", "dawgs"].includes(site) &&
            gamesAndEvents.map((node) => {
              const isEvent =
                _.get(node, "node.frontmatter.mdType", "") === "event";
              if (isEvent) {
                return (
                  <SliderEventItem
                    event={node}
                    hide={_.isEmpty(breakpoints)}
                    key={node.node._id}
                  />
                );
              }
              return (
                <SliderItem
                  hide={_.isEmpty(breakpoints)}
                  key={node.id}
                  game={node}
                  teamsByName={teamsByName}
                  gameDuration={GAME_DURATION_MINUTE}
                  watchLiveLink={watchLiveLink}
                  teamSeasonLink={teamSeasonLink}
                  site={site}
                  color={color}
                  homeTeams={homeTeams}
                />
              );
            })}
          {site === "diamondbacks" &&
            slicedGames.map((node) => {
              const league = allLeagues.find(
                (l) => l.node.frontmatter.name === node.frontmatter.competition
              );
              return (
                <SliderItem
                  hide={_.isEmpty(breakpoints)}
                  key={node.id}
                  game={node}
                  league={league}
                  teamsByName={teamsByName}
                  gameDuration={GAME_DURATION_MINUTE}
                  watchLiveLink={watchLiveLink}
                  teamSeasonLink={teamSeasonLink}
                  site={site}
                  color={color}
                />
              );
            })}
        </Carousel>
      )}
    </SliderContainer>
  );
};

export default ScheduleSlider;

const ItemContainerLink = styled.a`
  ${tw`block py-1.5 px-2 mb-1 text-sm border-r border-x-gray-200 min-w-[175px] transition-opacity`}
  ${({ $hide }) => ($hide ? tw`opacity-0` : tw`opacity-100 hover:opacity-70`)}
   ${({ disabled }) => disabled && tw`pointer-events-none`}
`;

const ItemContainerSpan = styled.span`
  ${tw`block py-1.5 px-2 mb-1 text-sm border-r border-x-gray-200 min-w-[175px] transition-opacity cursor-pointer`}
  ${({ $hide }) => ($hide ? tw`opacity-0` : tw`opacity-100 hover:opacity-70`)}
`;

const ItemContainerGatsbyLink = styled(Link)`
  ${tw`block py-1.5 px-2 mb-1 text-sm border-r border-x-gray-200 min-w-[175px] transition-opacity`}
  ${({ $hide }) => ($hide ? tw`opacity-0` : tw`opacity-100 hover:opacity-70`)}
`;
const ItemHeaderText = styled.div`
  ${tw`flex justify-between items-center text-white rounded px-2 py-1`}
  background-color: ${(props) => props.color}
`;
const Header = styled.div`
  ${tw`text-center flex-1 `}
  color: ${(props) => getContrastText(props.color)};
`;
const HeaderDate = styled.div`
  ${tw`font-bold px-1 text-sm whitespace-nowrap`}
`;
const StatsContainer = styled.div`
  ${tw`flex justify-between items-center mt-1`}
`;
const Time = styled.div`
  ${tw`text-sm text-gray-500 font-semibold text-ellipsis whitespace-nowrap overflow-hidden max-w-[110px] pr-1`}
`;
const Location = styled.div`
  ${tw`text-xs font-semibold text-gray-700 text-ellipsis whitespace-nowrap overflow-hidden max-w-[110px]`}
`;
const LogoContainer = styled.div`
  ${tw`w-8 h-5 overflow-hidden flex justify-center items-center`}
`;
const TeamsContainer = styled.div`
  ${tw`pr-1 flex flex-col justify-between`}
  ${({ isCompletedGame }) =>
    isCompletedGame ? tw`min-w-[85px] pr-3 pl-3` : tw`min-w-[85px]`}
`;
const FlexContainer = styled.div`
  ${tw`flex justify-between items-center my-1 gap-1`}
`;
const TeamAbbr = styled.div`
  ${tw`w-10 text-xs text-center overflow-hidden`}
  ${({ bold }) => bold && tw`font-bold`}
`;
const Score = styled.div`
  ${tw`font-light w-2 text-center text-gray-500`}
  ${({ bold }) => bold && tw`font-bold`}
`;
const LinkContainer = styled.span`
  ${tw`w-full flex flex-col -translate-y-[70px] bg-white border border-gray-400 rounded`}

  > a {
    ${tw`px-4 py-0.5 text-sm hover:bg-gray-100 inline-block text-gray-600 shadow-2xl font-bold`}
  }
`;

const EventNameContainer = styled.div`
  ${tw`font-bold mt-1 text-center`}
`;

const PlacholderContainer = styled.div`
  ${tw`w-6`}
`;

const SliderItem = ({
  game,
  teamsByName,
  gameDuration = 180,
  watchLiveLink,
  teamSeasonLink,
  league,
  hide,
  site,
  color,
  homeTeams = [],
}) => {
  const {
    homeTeam,
    awayTeam,
    numberSuffix,
    yr,
    rainOut,
    date,
    showBuyTicket,
    ticketLink,
    liveScoreLink,
    stats,
    field,
  } = game.frontmatter;
  const momentDate = moment.tz(date, TZ);
  const timePassed = moment.tz(TZ).diff(momentDate, "minutes");
  const isCompletedGame = timePassed > gameDuration;
  const isLiveGame = timePassed <= gameDuration && timePassed >= 0;
  const isFutureGame = timePassed < 0;

  const dateText = moment.tz(momentDate, TZ).format("MMM D");
  const timeText = moment.tz(momentDate, TZ).format("ddd h:mm A").toUpperCase();
  const timeP1Text = moment.tz(momentDate, TZ).format("dddd");
  const timeP2Text = moment.tz(momentDate, TZ).format("h:mm A").toUpperCase();

  const homeGame = homeTeams.includes(homeTeam.toLowerCase());
  const home = teamsByName[homeTeam.toLowerCase()];
  const away = teamsByName[awayTeam.toLowerCase()];

  // const homeTeamField = _.get(home, "node.frontmatter.location.fieldName")
  const homeTeamTown = _.get(home, "node.frontmatter.location.town");

  const headerColor = getHeaderColor(
    isCompletedGame,
    isLiveGame,
    isFutureGame,
    showBuyTicket,
    homeGame,
    site,
    color
  );

  const { score } = useStats({ inningStats: stats.inningStats });
  let finalScore = !hasEmptyDeep(stats.finalScore) ? stats.finalScore : score;

  let awayWinner = finalScore.away > finalScore.home;
  let homeWinner = finalScore.home > finalScore.away;
  const slug = `/schedule/${_.kebabCase(yr)}/${_.kebabCase(
    `${awayTeam}-${homeTeam}`
  )}-${moment(date).tz(TZ).format("YYYY-MM-DD")}${
    numberSuffix ? `-${numberSuffix}` : ""
  }`;

  const displayField = field ? extractContentInsideBrackets(field) : field;

  const awayLogo = getImage(_.get(away, "node.frontmatter.logo"));
  const homeLogo = getImage(_.get(home, "node.frontmatter.logo"));

  return (
    <LinkWrapper
      hide={hide}
      isCompletedGame={isCompletedGame}
      isLiveGame={isLiveGame}
      isFutureGame={isFutureGame}
      homeGame={homeGame}
      slug={slug}
      watchLiveLink={watchLiveLink}
      teamSeasonLink={teamSeasonLink}
      ticketLink={ticketLink}
      liveScoreLink={liveScoreLink}
      site={site}
    >
      {["dawgs", "academy"].includes(site) && isCompletedGame && (
        <ItemHeaderText color={headerColor}>
          <Header color={headerColor} site={site}>
            RECAP
          </Header>
          <HeaderDate>{dateText}</HeaderDate>
        </ItemHeaderText>
      )}
      {["dawgs", "academy"].includes(site) && isLiveGame && (
        <ItemHeaderText color={headerColor}>
          <Header color={headerColor} site={site}>
            WATCH LIVE
          </Header>
          <HeaderDate>{dateText}</HeaderDate>
        </ItemHeaderText>
      )}
      {["dawgs"].includes(site) &&
        isFutureGame &&
        (homeGame || showBuyTicket) && (
          <ItemHeaderText color={headerColor}>
            <Header color={headerColor} site={site}>
              BUY TICKETS
            </Header>
            <HeaderDate>{dateText}</HeaderDate>
          </ItemHeaderText>
        )}
      {["academy"].includes(site) &&
        isFutureGame &&
        (homeGame || showBuyTicket) && (
          <ItemHeaderText color={headerColor}>
            <Header color={headerColor} site={site}>
              <span>HOME GAME</span>
            </Header>
            <HeaderDate>{dateText}</HeaderDate>
          </ItemHeaderText>
        )}
      {["dawgs", "academy"].includes(site) &&
        isFutureGame &&
        !homeGame &&
        !showBuyTicket && (
          <ItemHeaderText color={headerColor}>
            <Header color={headerColor} site={site}>
              <span>AWAY GAME</span>
            </Header>
            <HeaderDate>{dateText}</HeaderDate>
          </ItemHeaderText>
        )}

      {["diamondbacks"].includes(site) && (
        <ItemHeaderText color={league.node.frontmatter.color}>
          <Header color={league.node.frontmatter.color} site={site}>
            {league.node.frontmatter.name}
          </Header>
          <HeaderDate>{dateText}</HeaderDate>
        </ItemHeaderText>
      )}
      <StatsContainer>
        <div style={{ flex: 1 }}>
          {isCompletedGame ? (
            <Time>FINAL</Time>
          ) : (
            <>
              {rainOut ? (
                <Time>RAIN OUT</Time>
              ) : (
                <>
                  {["dawgs"].includes(site) && <Time>{timeText}</Time>}
                  {["diamondbacks", "academy"].includes(site) && (
                    <>
                      <Time style={{ textAlign: "center" }}>{timeP1Text}</Time>
                      <Time style={{ textAlign: "center" }}>{timeP2Text}</Time>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {/* <Location>{homeTeamField}</Location> */}
          {["dawgs"].includes(site) && <Location>{homeTeamTown}</Location>}
          {/* {["academy"].includes(site) && displayField && (
            <Location>{displayField}</Location>
          )} */}
        </div>
        <TeamsContainer isCompletedGame={isCompletedGame}>
          <FlexContainer>
            <LogoContainer>
              {awayLogo ? (
                <GatsbyImage
                  alt="logo"
                  image={awayLogo}
                  style={{ width: "100%", height: "100%" }}
                  objectFit="contain"
                  objectPosition="50% 50%"
                />
              ) : (
                <PlacholderContainer>
                  <img
                    src={logoPlaceholder}
                    alt="placeholder"
                    style={{ width: "100%", height: "auto" }}
                    loading="lazy"
                  />
                </PlacholderContainer>
              )}
            </LogoContainer>
            <TeamAbbr bold={awayWinner}>
              {_.get(away, "node.frontmatter.location.abbreviation", "")}
            </TeamAbbr>
            {isCompletedGame && !_.isNil(finalScore.away) && (
              <Score bold={awayWinner}>{finalScore.away}</Score>
            )}
          </FlexContainer>
          <FlexContainer>
            <LogoContainer>
              {homeLogo ? (
                <GatsbyImage
                  alt="logo"
                  image={homeLogo}
                  style={{ width: "100%", height: "100%" }}
                  objectFit="contain"
                  objectPosition="50% 50%"
                />
              ) : (
                <PlacholderContainer>
                  <img
                    src={logoPlaceholder}
                    alt="placeholder"
                    style={{ width: "100%", height: "auto" }}
                    loading="lazy"
                  />
                </PlacholderContainer>
              )}
            </LogoContainer>
            <TeamAbbr bold={homeWinner}>
              {_.get(home, "node.frontmatter.location.abbreviation", "")}
            </TeamAbbr>
            {isCompletedGame && !_.isNil(finalScore.home) && (
              <Score bold={homeWinner}>{finalScore.home}</Score>
            )}
          </FlexContainer>
        </TeamsContainer>
      </StatsContainer>
    </LinkWrapper>
  );
};

const SliderEventItem = ({ event, hide }) => {
  const { name, startDate, endDate, highlightColor } = event.node.frontmatter;
  const formattedStartDate = moment
    .tz(startDate, TZ)
    .format("MMM D")
    .toUpperCase();
  const headerColor = highlightColor;

  return (
    <LinkWrapper hide={hide} ticketLink="#" disabled={true}>
      <ItemHeaderText color={headerColor}>
        <Header color={headerColor} site={site}>
          WATCH LIVE
        </Header>
        <HeaderDate>{formattedStartDate}</HeaderDate>
      </ItemHeaderText>
      <FlexContainer>
        <EventNameContainer>{name}</EventNameContainer>
      </FlexContainer>
    </LinkWrapper>
  );
};

const LinkWrapper = ({
  children,
  isCompletedGame,
  isLiveGame,
  isFutureGame,
  homeGame,
  slug,
  watchLiveLink,
  teamSeasonLink,
  ticketLink,
  liveScoreLink,
  hide,
  disabled,
  site,
}) => {
  const [open, setOpen] = useState(false);
  if (
    isCompletedGame ||
    (isFutureGame && !homeGame) ||
    ["diamondbacks", "academy"].includes(site)
  ) {
    return (
      <ItemContainerGatsbyLink to={slug} $hide={hide}>
        {children}
      </ItemContainerGatsbyLink>
    );
  } else if (isLiveGame) {
    return (
      <ItemContainerSpan $hide={hide} onClick={() => setOpen(!open)}>
        <Popover
          isOpen={open}
          content={
            <LinkContainer>
              <a href={watchLiveLink || teamSeasonLink} target="_blank">
                Watch Live
              </a>
              <a href={liveScoreLink || teamSeasonLink} target="_blank">
                Box Score
              </a>
            </LinkContainer>
          }
          // padding={8}
          // boundaryInset={16}
          positions={["bottom"]}
          onClickOutside={() => setOpen(false)}
        >
          <span>{children}</span>
        </Popover>
      </ItemContainerSpan>
    );
  } else {
    return (
      <ItemContainerLink
        href={ticketLink}
        target="_blank"
        $hide={hide}
        disabled={disabled}
      >
        {children}
      </ItemContainerLink>
    );
  }
};

const getHeaderColor = (
  isCompletedGame,
  isLiveGame,
  isFutureGame,
  showBuyTicket,
  homeGame,
  site,
  color
) => {
  if (!isLiveGame && ["diamondbacks"].includes(site)) {
    return color;
  }

  if (isCompletedGame) {
    return "#212121";
  } else if (isFutureGame && (homeGame || showBuyTicket)) {
    return "#EC1F27";
  } else if (isFutureGame && !homeGame && !showBuyTicket) {
    return "#9e9e9e";
  } else if (isLiveGame) {
    return "#fbc02d";
  } else {
    return "#000000";
  }
};

const getInitialGameIndex = (sortedGames, gameDuration) => {
  if (_.isEmpty(sortedGames)) {
    return 0;
  }

  // find the the first game that is currently on going or in the furture
  const currentDate = moment();
  let gameIndex = sortedGames.findIndex((game) => {
    const { date } = game.frontmatter;
    const momentDate = moment(date);
    const timePassed = currentDate.diff(momentDate, "minutes");
    const match = timePassed <= gameDuration;
    return match;
  });
  if (gameIndex < 0) {
    return Math.max(sortedGames.length - 7, 0);
  } else if (gameIndex === 0) {
    return gameIndex;
  } else {
    // if last game before this game is more than 6 month old do not move index
    const lastGameDate = _.get(
      sortedGames,
      `${gameIndex - 1}.frontmatter.date`
    );
    const isOld = moment().diff(moment(lastGameDate), "months") > 6;
    if (isOld) {
      return gameIndex;
    } else {
      return gameIndex - 1;
    }
  }
};

const getSlidesToShow = (breakpoints) => {
  const numberOfTrues = Object.values(breakpoints).reduce(
    (a, b) => a + (b ? 1 : 0),
    0
  );
  let slidesToShow = 1.5;
  switch (numberOfTrues) {
    case 0:
      // mobile
      slidesToShow = 1.5;
      break;
    case 1:
      // > 640px
      slidesToShow = 3;
      break;
    case 2:
      // > 768px
      slidesToShow = 3.5;
      break;
    case 3:
      // > 1024px
      slidesToShow = 5;
      break;
    case 4:
      // > 1280px
      slidesToShow = 6;
      break;
    case 5:
      // > 1536px
      slidesToShow = 7;
      break;
    default:
      break;
  }
  return slidesToShow;
};
